import { isTruthy, useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import {
  NoteAgentDetail,
  UpdateNoteMutation,
  UpdateNoteMutationVariables,
} from "@/graphql/generated/graphql";
import { UpdateNote } from "@/graphql/mutations/UpdateNote";
import { GetTagsAndNotes } from "@/graphql/queries/GetTagsAndNotes";
import { useCall } from "@/hooks/useCall/useCall";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { usePathParams } from "@/states/path_param/path_params";

export function useUpdateNote() {
  const { callId } = usePathParams();

  const { t } = useTranslation();
  const { track } = useTrack();

  const { show } = useToast();

  const { call } = useCall();

  const [updateMutation, { loading }] = useGraphMutation(UpdateNote, {
    onCompleted() {
      show({
        message: t("note_updated"),
        variant: "success",
      });
      track("conversation_note_updated", {
        call_id: call?.id,
      });
    },
    onError(e) {
      show({
        variant: "critical",
        message: e.message,
      });
    },
  });

  const updateNote = ({ noteId, content }: UpdateNoteMutationVariables) => {
    const optimisticUpdate: UpdateNoteMutation = {
      updateNote: {
        id: noteId,
        content,
      },
    };

    updateMutation({
      variables: {
        noteId,
        content,
      },
      optimisticResponse: optimisticUpdate,
      update(cache, { data }) {
        cache.modify({
          id: cache.identify({ __typename: "NoteAgentDetail", id: noteId }),
          fields: {
            content() {
              if (!data) {
                return;
              }

              return data.updateNote.content;
            },
          },
        });

        if (!data) {
          return;
        }

        const readTagsAndNotes = cache.readQuery({
          query: GetTagsAndNotes,
          variables: {
            callId,
          },
        });

        cache.writeQuery({
          query: GetTagsAndNotes,
          data: {
            getTagsAndNotes: {
              __typename: "TagsAndNotes",
              tags: readTagsAndNotes?.getTagsAndNotes?.tags ?? [],
              notes:
                readTagsAndNotes?.getTagsAndNotes?.notes
                  .filter(isTruthy)
                  .map((note: NoteAgentDetail) =>
                    note.id !== noteId
                      ? note
                      : {
                          ...note,
                          id: data.updateNote.id,
                          content: data.updateNote.content,
                        }
                  ) ?? [],
            },
          },
          variables: {
            callId,
          },
        });
      },
    });
  };

  return { updateNote, loading };
}
